<template>
    <NuxtLayout name="malt-base-layout" hide-gdpr-popup :hide-host-redirection-dialog="hideHostRedirectionDialog" hide-terms-and-conditions-popup>
        <VJoyFunnel
            v-if="isHydrated"
            class="signup-client signup-client__custom"
            :class="{'no-padding': isFunnelWithoutPadding}"
            layout-width="medium"
            :hide-shapes="props.hideShapes"
        >
            <template #funnel-header>
                <VJoyFunnelHeader>
                    <template #funnel-header-left>
                        <div class="signup-client__malt-logo">
                            <MaltImg
                                src="/navbar/logos/malt-logo-red.svg?vh=9690b2"
                                :alt="$t('signup.client.funnel.header.home.link.img.alt')"
                                height="40"
                            />
                            <template v-if="!loggedIn">
                                <VJoyDialogTrigger :dialog="cancelWorkflowConfirmDialog">
                                    <a href="/" :title="$t('signup.client.funnel.header.home.link.title')" data-testid="header-logo" @click.prevent />
                                </VJoyDialogTrigger>
                                <VJoyDialog
                                    ref="cancelWorkflowConfirmDialog"
                                    pattern-confirm
                                    :confirm-text="$t('signup.client.cancellation.confirm.cta')"
                                    :cancel-text="$t('signup.client.cancellation.cancel.cta')"
                                    @dialog:confirm="cancelSignup"
                                >
                                    <template #dialog-header>{{ $t('signup.client.cancellation.confirmation.title') }}</template>
                                    <template #dialog-body>
                                        {{ $t('signup.client.cancellation.confirmation.body') }}
                                    </template>
                                </VJoyDialog>
                            </template>
                        </div>
                    </template>
                    <template #funnel-header-right>
                        <VJoyIconButton
                            v-if="displayCloseButton"
                            icon="cross"
                            variant="ghost"
                            @click.prevent="handleClickOnExitWithTracking('/project-client/find-freelancers')"
                        />
                        <div v-if="isLoginCtaVisible" class="signup-client__custom__header-right">
                            <div class="signup-client__custom__header-right__info">
                                {{ $t('signup.client.funnel.header.login.cta.context') }}
                            </div>
                            <VJoyButton size="small" variant="secondary" :href="signinHref" data-testid="signin-button">
                                {{ $t('signup.client.funnel.header.login.cta') }}
                            </VJoyButton>
                        </div>
                        <ClientOnly>
                            <div v-if="isCompanyVisible" class="signup-client__company">
                                <VJoyCompanyAvatar
                                    :company-name="companyName"
                                    :img-src="companyLogoUrl"
                                    :img-alt="$t('signup.client.funnel.header.company.logo.alt', [companyName])"
                                    size="medium"
                                    data-testid="signup-company-avatar"
                                />
                                <strong>{{ companyName }}</strong>
                            </div>
                        </ClientOnly>
                    </template>
                </VJoyFunnelHeader>
                <div v-if="isAiBannerVisible" class="ai-banner">
                    <div class="ai-banner__content">
                        <VJoyIcon size="small" name="sparkles" class="joy-color-neutral-10" />
                        <span>{{ t('signup.client.ai.banner') }}</span>
                    </div>
                </div>
            </template>
            <template #funnel-stepper>
                <VJoyStepper
                    v-if="!isAiBannerVisible && currentStep"
                    :step="currentStep.number"
                    :sub-step="currentSubStepIndex"
                    class="signup-client__custom__stepper"
                >
                    <template v-if="$ff('signup-client-invert-user-details-and-choose-company-steps')">
                        <VJoyStep
                            v-for="step in STEPS_USER_DETAILS_INVERTED_WITH_COMPANY_CHOICE"
                            :id="`step-${step.name}`"
                            :key="`${step.name}`"
                            :sub-steps="subSteps"
                        >
                            {{ t(step.label) }}
                        </VJoyStep>
                    </template>
                    <template v-else>
                        <VJoyStep v-for="step in STEPS" :id="`step-${step.name}`" :key="`${step.name}`" :sub-steps="subSteps">
                            {{ t(step.label) }}
                        </VJoyStep>
                    </template>
                </VJoyStepper>
            </template>
            <VJoyTemplate>
                <template #template-main>
                    <div class="signup-client__custom__content">
                        <form id="funnel-form" class="signup-client-form" novalidate @submit.prevent="onNextClick">
                            <slot />
                        </form>
                        <VJoyFunnelFooter v-if="!props.disableFooter">
                            <template v-if="(isSkipCtaVisible && isMobile) || isBackCtaVisible || isNotMyTeamButtonVisible" #footer-left>
                                <VJoyButton v-if="isSkipCtaVisible && isMobile" variant="ghost" data-testid="signup-skip-button" @click="onSkipClick">
                                    {{ skipCTALabel }}
                                </VJoyButton>
                                <VJoyButton v-if="isBackCtaVisible" variant="secondary" data-testid="signup-back-button" @click="onPreviousClick">
                                    {{ backCTALabel }}
                                </VJoyButton>
                                <VJoyButton
                                    v-if="isNotMyTeamButtonVisible"
                                    variant="ghost"
                                    data-testid="signup-not-my-team-button"
                                    :label="$t('signup.client.funnel.footer.not.my.team.button')"
                                    @click="onNotMyTeamClick"
                                />
                            </template>
                            <template #footer-right>
                                <VJoyButton
                                    v-if="isSkipCtaVisible && !isMobile"
                                    variant="ghost"
                                    data-testid="signup-skip-button"
                                    @click="onSkipClick"
                                >
                                    {{ skipCTALabel }}
                                </VJoyButton>
                                <VJoyButton
                                    type="submit"
                                    form="funnel-form"
                                    variant="main"
                                    :disabled="isNextCtaDisabled"
                                    :loading="pendingRequest"
                                    data-testid="signup-continue-button"
                                >
                                    {{ nextCTALabel }}
                                </VJoyButton>
                            </template>
                        </VJoyFunnelFooter>
                    </div>
                </template>
                <template v-if="!props.disableSideContent" #template-sidebar>
                    <div :key="animationTransitionKey" class="signup-client__custom__side">
                        <div class="signup-client__custom__side-shade" />
                        <div class="signup-client__custom__side-content">
                            <slot name="sidecontent" />
                        </div>
                    </div>
                </template>
            </VJoyTemplate>
        </VJoyFunnel>
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {navigateTo} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {definePageMeta, useAuth, useDataTracking, useFunnelSteps, useHydrationState, useTranslation} from '#imports';
    import {useMediaQuery} from '@malt/utils';
    import {
        VJoyButton,
        VJoyCompanyAvatar,
        VJoyDialog,
        VJoyDialogTrigger,
        VJoyFunnel,
        VJoyFunnelFooter,
        VJoyFunnelHeader,
        VJoyIcon,
        VJoyStep,
        VJoyStepper,
        VJoyTemplate,
        VJoyIconButton,
    } from '@maltjoy/core-vue';
    import {eventBus} from '~/lib/events';
    import {FunnelPageName, STEPS, STEPS_USER_DETAILS_INVERTED_WITH_COMPANY_CHOICE} from '~/lib/steps';
    import {useContextStore} from '~/store/context.store';
    import {useSignupStore} from '~/store/signup.store';
    import {useUserQualificationStore} from '~/store/user-qualification.store';

    definePageMeta({
        layout: false,
    });

    const props = withDefaults(defineProps<{disableFooter?: boolean; disableSideContent?: boolean; hideShapes?: boolean}>(), {
        disableFooter: false,
        disableSideContent: false,
        hideShapes: true,
    });

    // Refs
    const cancelWorkflowConfirmDialog = ref<InstanceType<typeof VJoyDialog>>();

    // Composables
    const {
        currentPage,
        currentStep,
        currentSubStepIndex,
        isAiBannerVisible,
        isBackCtaVisible,
        isNotMyTeamButtonVisible,
        isCompanyVisible,
        isFunnelWithoutPadding,
        isLoginCtaVisible,
        isNextCtaDisabled,
        isSkipCtaVisible,
        signinHref,
        subSteps,
    } = useFunnelSteps();

    const userQualificationStore = useUserQualificationStore();
    const {currentQuestionIndex} = storeToRefs(userQualificationStore);
    const {track} = useDataTracking();

    // Stores
    const auth = useAuth();
    const {loggedIn} = storeToRefs(auth);
    const contextStore = useContextStore();
    const {companyName, companyLogoUrl} = storeToRefs(contextStore);
    const signupStore = useSignupStore();
    const {pendingRequest, emailValidity} = storeToRefs(signupStore);

    // Composables
    const {isHydrated} = useHydrationState();
    const {matchMedia} = useMediaQuery();
    const {t} = useTranslation();

    // Computed values
    const isMobile = computed<boolean>(() => matchMedia.value.max.Xxs);
    const animationTransitionKey = computed(() =>
        currentPage.value?.name !== FunnelPageName.USER_QUALIFICATION ? currentPage.value?.name : currentQuestionIndex.value,
    );
    const hideHostRedirectionDialog = computed(() => currentPage.value?.name !== FunnelPageName.EMAIL);

    /**
     * TODO: do not rely on route but the current step / sub-step name
     */
    const backCTALabel = computed(() => {
        if (currentPage.value?.name === FunnelPageName.PUBLIC_EMAIL_WARNING) {
            return t('signup.client.email.switch.to.work.email.page.secondary.cta');
        }
        return t('signup.client.funnel.footer.back.button');
    });
    const nextCTALabel = computed(() => {
        if (currentPage.value?.name === FunnelPageName.COMPANY_CHOICE) {
            return t('signup.client.funnel.footer.join.button');
        }

        if (currentPage.value?.name === FunnelPageName.EMAIL && emailValidity.value === 'VALID_NON_WORK_EMAIL') {
            return t('signup.client.funnel.footer.continue.anyway.button');
        }

        if (currentPage.value?.name === FunnelPageName.PUBLIC_EMAIL_WARNING) {
            return t('signup.client.email.switch.to.work.email.page.primary.cta');
        }

        return t('signup.client.funnel.footer.continue.button');
    });
    const skipCTALabel = t('signup.client.funnel.footer.skip.button');

    const displayCloseButton = computed(() => currentPage.value?.name === FunnelPageName.WELCOME);

    async function handleClickOnExitWithTracking(link: string) {
        await track('signup_client_welcome_screen_clicked', {
            welcome_screen_version: 'default-ai-match-wording',
            welcome_screen_action: 'close_screen',
        });
        return await navigateTo(link, {external: true});
    }

    // Event handlers
    function onPreviousClick() {
        eventBus.emit('funnel:previous:click');
    }

    function onNextClick() {
        eventBus.emit('funnel:next:click');
    }

    function onSkipClick() {
        eventBus.emit('funnel:skip:click');
    }

    function cancelSignup() {
        eventBus.emit('funnel:cancel');
    }
    function onNotMyTeamClick() {
        eventBus.emit('funnel:not-my-team:click');
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .ai-banner {
        width: 100%;
        height: 55px;
        background: var(--joy-color-ai-gradient-30);
        padding: var(--joy-core-spacing-3) var(--joy-core-spacing-4);

        @include mq.screen_sm() {
            height: 65px;
        }

        &__content {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: var(--joy-core-spacing-2);
            height: 100%;

            span {
                line-height: var(--joy-line-height-small);
                font-weight: var(--joy-font-weight-bold);
                font-size: var(--joy-font-size-primary-600);
                color: var(--joy-color-neutral-10);
            }
        }
    }

    .joy-funnel__scrollable {
        // needs to be included in the component itself
        overflow-x: hidden !important;
    }

    .joy-funnel__content--medium:has(.large-layout) {
        width: 100%;
        max-width: var(--funnel-content-width-large);
    }

    .signup-client {
        &__title {
            margin-bottom: var(--joy-core-spacing-2);
            @include text.getFontBySize(secondary-200);

            @include mq.screen_sm(min) {
                @include text.getFontBySize(secondary-400);
            }
        }

        &__subtitle {
            @include text.getFontBySize(primary-600);
        }

        &.joy-funnel {
            background-color: var(--joy-color-neutral-10);
        }

        &.no-padding {
            .joy-funnel__content {
                padding-right: 0 !important;
                padding-left: 0 !important;
            }
        }

        &__malt-logo {
            position: relative;
            height: 40px;
            display: flex;
            align-items: center;

            > img {
                height: 28px;
            }

            a {
                position: absolute;
                inset: 0;
            }
        }

        &__company {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-3);
        }

        &__custom {
            --custom-content-width: 600px;
            --custom-side-width: 380px;
            --custom-side-bg-width: 545px;

            &__header-right {
                display: flex;
                align-items: center;
                gap: var(--joy-core-spacing-3);
                color: var(--joy-color-neutral-60);

                &__info {
                    @include text.getFontBySize(primary-300);

                    @include mq.screen_md() {
                        display: none;
                    }
                }
            }

            .joy-funnel__scrollable {
                display: flex;
                align-items: center;
            }

            .joy-funnel__stepper {
                width: calc(100% - 350px) !important;
                max-width: 100vw !important;
                padding: 0;
                margin: 0;
                z-index: 0;

                @include mq.screen_md() {
                    width: var(--custom-content-width) !important;
                    margin: 0 auto;
                }
            }

            &__content {
                @include mq.screen_lg(min) {
                    margin: 0 auto;
                    width: var(--custom-content-width);
                    max-width: 100vw;
                }
            }

            &__side {
                width: var(--custom-side-width);
                padding: 0 var(--joy-core-spacing-9) 0 var(--joy-core-spacing-1);
                display: flex;
                align-content: center;
                align-items: center;
                height: 100%;

                &-shade {
                    position: fixed;
                    top: 0px;
                    right: -100px;
                    width: var(--custom-side-bg-width);
                    height: 100%;
                    transform: skew(-5deg);
                    background-color: var(--joy-color-secondary-30);
                    border-radius: 180px 0 0 0;
                    z-index: -1;
                    animation: slide-in 0.3s ease-in-out;
                }

                &-content {
                    animation: side-content-slide-in 0.4s ease-in-out;
                }
            }

            .joy-funnel__content,
            .joy-template__wrapper {
                @include mq.screen_lg(min) {
                    width: 100%;
                    max-width: 100%;
                    margin: 0;
                    padding: 0;
                }
            }

            .joy-funnel__scrollable {
                padding: 210px 0;
            }

            .joy-step__label {
                margin-left: var(--joy-core-spacing-3);
            }

            .joy-template__content {
                gap: 0;
            }

            .joy-template {
                .joy-template-slot-sidebar {
                    &,
                    .joy-title-brand {
                        color: var(--joy-color-neutral-10);
                    }

                    @include mq.screen_md() {
                        display: none;
                    }
                }
            }
        }
    }

    @keyframes side-content-slide-in {
        from {
            transform: translateX(100%);
        }

        to {
            transform: translateX(0%);
        }
    }

    @keyframes slide-in {
        from {
            transform: translateX(100%) skew(-5deg);
        }

        to {
            transform: translateX(0%) skew(-5deg);
        }
    }
</style>
